import { useState } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Filter, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

type OwnershipFilterType = 'all' | 'created' | 'created_shared';

interface LeadFilterDialogProps {
  value: OwnershipFilterType;
  onChange: (value: OwnershipFilterType) => void;
}

const filterOptions: Array<{
  value: OwnershipFilterType;
  label: string;
  description: string;
  icon?: React.ReactNode;
}> = [
  {
    value: 'all',
    label: 'All Leads',
    description: 'Show all leads you have access to',
  },
  {
    value: 'created',
    label: 'My Leads',
    description: 'Only leads created by you',
  },
  {
    value: 'created_shared',
    label: 'My + Shared',
    description: 'Leads created by or shared with you',
  },
];

export function LeadFilterDialog({ value, onChange }: LeadFilterDialogProps) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className={cn(
            'relative',
            value !== 'all' && 'border-primary/50 bg-primary/5'
          )}
          title="Filter leads by ownership"
        >
          <Filter className="h-4 w-4" />
          {value !== 'all' && (
            <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-primary" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-0">
        <div className="space-y-1 p-3">
          <div className="px-2 py-1.5">
            <h3 className="text-sm font-semibold text-foreground">
              Filter by Ownership
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Choose which leads to display
            </p>
          </div>

          <div className="border-t pt-2 space-y-1.5">
            {filterOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                className={cn(
                  'w-full text-left px-3 py-2.5 rounded-md transition-colors',
                  'hover:bg-accent',
                  value === option.value
                    ? 'bg-primary/10 border border-primary/30'
                    : 'border border-transparent'
                )}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <p
                      className={cn(
                        'text-sm font-medium',
                        value === option.value
                          ? 'text-primary'
                          : 'text-foreground'
                      )}
                    >
                      {option.label}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {option.description}
                    </p>
                  </div>
                  {value === option.value && (
                    <Check className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
